<template>
  <div>
    <b-card
      v-if="taskItem && taskItem.id"
    >
      <!-- Başlık + Aksiyonlar -->
      <div class="d-flex flex-wrap justify-content-between align-items-center border-bottom pb-1 mb-1">
        <h2 class="text-primary mb-0">
          {{ taskItem.title }}
        </h2>
        <!-- Dar ekranda düğmeler başlığın altına iner -->
        <div class="ml-sm-2 mt-50 mt-sm-0 flex-sm-shrink-0">
          <template v-if="canManage && !isLocked">
            <b-button
              variant="success"
              size="sm"
              class="mr-1"
              pill
              :disabled="saving || !canTransitionTo('completed')"
              :title="completeDisabledReason"
              @click="openCompleteModal"
            >
              <feather-icon icon="CheckIcon" />
              {{ $t('tasks.detail.complete') }}
            </b-button>
            <b-button
              variant="danger"
              size="sm"
              class="mr-1"
              pill
              :disabled="saving || !canTransitionTo('cancelled')"
              @click="setStatus('cancelled')"
            >
              <feather-icon icon="XIcon" />
              {{ $t('tasks.detail.cancel') }}
            </b-button>
          </template>
          <b-button
            v-if="canManage && currentStatus === 'completed' && isReopenable"
            variant="warning"
            size="sm"
            class="mr-1"
            pill
            :disabled="saving"
            :title="reopenRemainingText"
            @click="confirmReopen"
          >
            <feather-icon icon="RotateCcwIcon" />
            {{ $t('tasks.detail.reopen') }}
          </b-button>
          <b-button
            v-if="canEdit"
            variant="primary"
            size="sm"
            pill
            :to="isLocked ? null : ('/app/tasks/edit/' + taskItem.id)"
            :disabled="isLocked"
          >
            <feather-icon icon="EditIcon" />
            {{ $t('common.edit') }}
          </b-button>
        </div>
      </div>
      <b-row>
        <b-col
          cols="12"
          md="10"
        >
          <b-table-simple
            bordered
          >
            <b-tbody>
              <b-tr>
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('fields.project') }}
                </b-th>
                <b-td>
                  <router-link
                    v-if="canViewProject"
                    :to="`/app/projects/detail/${taskItem.id_projects}`"
                  >
                    {{ taskItem.project }}
                  </router-link>
                  <template v-else>
                    {{ taskItem.project || '—' }}
                  </template>
                </b-td>
              </b-tr>
              <b-tr>
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('tasks.detail.responsible') }}
                </b-th>
                <b-td>
                  {{ managerNames || '—' }}
                </b-td>
              </b-tr>
              <b-tr>
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('fields.department') }}
                </b-th>
                <b-td>
                  {{ taskItem.department || '—' }}
                </b-td>
              </b-tr>
              <b-tr>
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('fields.team') }}
                </b-th>
                <b-td>
                  {{ taskItem.team || '—' }}
                </b-td>
              </b-tr>
              <b-tr>
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('tasks.columns.watchers') }}
                </b-th>
                <b-td>
                  {{ watcherNames || '—' }}
                </b-td>
              </b-tr>
              <b-tr>
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('fields.startDate') }}
                </b-th>
                <b-td>
                  {{ taskItem.start_date ? moment(taskItem.start_date).format('DD.MM.YYYY') : '—' }}
                </b-td>
              </b-tr>
              <b-tr>
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('fields.targetDate') }}
                </b-th>
                <b-td>
                  {{ taskItem.target_date ? moment(taskItem.target_date).format('DD.MM.YYYY') : '—' }}
                </b-td>
              </b-tr>
              <b-tr v-if="taskItem.completed_date">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('tasks.columns.completedDate') }}
                </b-th>
                <b-td>
                  {{ moment(taskItem.completed_date).format('DD.MM.YYYY') }}
                </b-td>
              </b-tr>
              <b-tr v-if="taskItem.completed_by_name">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('tasks.detail.completedBy') }}
                </b-th>
                <b-td>
                  {{ taskItem.completed_by_name }}
                </b-td>
              </b-tr>
              <b-tr v-if="taskItem.closing_note">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('tasks.detail.closingNote') }}
                </b-th>
                <b-td>
                  {{ taskItem.closing_note }}
                </b-td>
              </b-tr>
              <b-tr v-if="taskItem.reopened_at">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('tasks.detail.reopenedAt') }}
                </b-th>
                <b-td>
                  {{ moment(taskItem.reopened_at).format('DD.MM.YYYY HH:mm') }}
                </b-td>
              </b-tr>
              <b-tr v-if="taskItem.reopened_by_name">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('tasks.detail.reopenedBy') }}
                </b-th>
                <b-td>
                  {{ taskItem.reopened_by_name }}
                </b-td>
              </b-tr>
              <b-tr v-if="taskItem.cancelled_date">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('tasks.columns.cancelledDate') }}
                </b-th>
                <b-td>
                  {{ moment(taskItem.cancelled_date).format('DD.MM.YYYY') }}
                </b-td>
              </b-tr>
              <b-tr v-if="taskItem.parent_task">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('tasks.detail.parentTask') }}
                </b-th>
                <b-td>
                  {{ taskItem.parent_task }}
                </b-td>
              </b-tr>
              <b-tr>
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('fields.description') }}
                </b-th>
                <b-td>
                  {{ taskItem.description? taskItem.description : '-' }}
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </b-col>
        <b-col
          cols="12"
          md="2"
        >
          <div class="border p-1 rounded text-center mb-1">
            <div
              class="font-weight-bolder"
              style="margin-bottom: 5px"
            >
              {{ $t('fields.status') }}
            </div>
            <b-badge
              class="w-100"
              :variant="currentStatus === 'open' ? 'success' : 'info'"
            >
              {{ taskStatusLabel }}
            </b-badge>
          </div>
          <div class="border p-1 rounded text-center mb-1">
            <div
              class="font-weight-bolder"
              style="margin-bottom: 5px"
            >
              {{ $t('fields.priority') }}
            </div>
            <b-badge
              variant="warning"
              class="w-100"
            >
              <priority-text :priority-id="taskItem.id_task_priorities" />
            </b-badge>
          </div>
          <div class="border p-1 rounded text-center">
            <div
              class="font-weight-bolder"
              style="margin-bottom: 5px"
            >
              {{ $t('tasks.columns.subtasks') }}
            </div>
            <b-badge
              variant="info"
              class="w-100"
            >
              {{ $t('tasks.detail.subtaskProgress', { done: taskItem.subtaskCompletedCount || 0, total: taskItem.subtaskCount || 0 }) }}
            </b-badge>
          </div>
        </b-col>
      </b-row>
    </b-card>
    <b-alert
      :show="notFound"
      variant="warning"
    >
      <div class="alert-body">
        {{ $t('tasks.detail.notFound') }}
      </div>
    </b-alert>

    <!-- Tamamlama modalı -->
    <b-modal
      v-model="completeModal"
      :title="$t('tasks.detail.completeTitle')"
      size="md"
      no-close-on-backdrop
      no-close-on-esc
      centered
      @hidden="onCompleteModalHidden"
    >
      <div>
        <label class="d-block font-weight-bold mb-1">
          {{ $t('tasks.detail.closingNote') }} <span class="text-danger">*</span>
        </label>
        <b-form-textarea
          v-model="closingNote"
          :placeholder="$t('tasks.detail.closingNotePlaceholder')"
          rows="4"
          :state="closingNoteError ? false : null"
        />
        <b-form-invalid-feedback :state="!closingNoteError">
          {{ closingNoteError }}
        </b-form-invalid-feedback>
      </div>
      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          :disabled="completeSaving"
          @click="completeModal = false"
        >
          {{ $t('common.cancel') }}
        </b-button>
        <b-button
          variant="success"
          :disabled="completeSaving"
          @click="submitComplete"
        >
          <b-spinner
            v-if="completeSaving"
            small
            class="mr-1"
          />
          {{ $t('tasks.detail.complete') }}
        </b-button>
      </template>
    </b-modal>

    <loading v-if="loading || !(taskItem && taskItem.id)" />
    <subtasks-list
      v-else-if="taskItem && taskItem.id"
      :id-tasks="taskItem.id"
      :locked="isLocked"
    />
  </div>
</template>

<script>
import {
  BAlert, BCard, BRow, BCol, BButton, BModal, BFormTextarea, BFormInvalidFeedback, BSpinner, BBadge, BTableSimple, BTbody, BTr,
  BTh, BTd,
} from 'bootstrap-vue'
import Loading from '@/layouts/components/common/Loading.vue'
import SubtasksList from '@/views/Tasks/Components/SubtasksList.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { canManageTask, canEditTask } from '@/auth/taskPermissions'
import PriorityText from '@/components/PriorityText.vue'
import { definitionLabel, definitionId, statusSlug } from '@/libs/definitions'

export default {
  name: 'TasksDetail',
  components: {
    BAlert,
    BCard,
    BRow,
    BCol,
    BButton,
    BModal,
    BFormTextarea,
    BFormInvalidFeedback,
    BSpinner,
    BBadge,
    BTableSimple,
    BTbody,
    BTr,
    BTh,
    BTd,
    Loading,
    SubtasksList,
    PriorityText,
  },
  data() {
    return {
      loaded: false,
      saving: false,
      completeModal: false,
      completeSaving: false,
      closingNote: '',
      closingNoteError: null,
    }
  },
  computed: {
    taskItem() {
      return this.$store.getters['tasks/dataItem']
    },
    notFound() {
      return this.loaded && !this.loading && !this.taskItem?.id
    },
    canViewProject() {
      return !!this.taskItem.id_projects && !!this.taskItem.project && this.$can('read', 'PROJECTS_LIST')
    },
    loading() {
      return this.$store.getters['tasks/loading']
    },
    canManage() {
      return canManageTask(this.taskItem)
    },
    canEdit() {
      return canEditTask(this.taskItem) && this.$can('read', 'TASKS_EDIT')
    },
    currentStatus() {
      return statusSlug('task', this.taskItem)
    },
    isLocked() {
      return ['completed', 'cancelled'].includes(this.currentStatus)
    },
    managerNames() {
      const users = this.taskItem?.manager_users
      if (!users || !users.length) return null
      return users.map(u => u.name).join(', ')
    },
    watcherNames() {
      const watchers = this.taskItem?.watchers
      if (!watchers || !watchers.length) return null
      return watchers.map(w => (w.watcher_type === 'user' ? w.name : w.team)).join(', ')
    },
    taskStatusLabel() {
      return definitionLabel('task', this.taskItem?.task_status_slug, this.taskItem?.task_status)
    },
    hasOpenSubtasks() {
      const list = this.$store.getters['subtasks/dataList'] || []
      return list.some(s => statusSlug('subtask', s) === 'open')
    },
    completeDisabledReason() {
      if (this.hasOpenSubtasks) return this.$t('tasks.detail.openSubtasksBlock')
      return null
    },
    isReopenable() {
      if (this.currentStatus !== 'completed' || !this.taskItem.completed_date) return false
      return this.moment().diff(this.moment(this.taskItem.completed_date), 'hours', true) < 24
    },
    reopenRemainingText() {
      if (!this.taskItem.completed_date) return ''
      const leftMinutes = Math.ceil((24 * 60) - this.moment().diff(this.moment(this.taskItem.completed_date), 'minutes', true))
      if (leftMinutes <= 0) return this.$t('tasks.detail.reopenExpired')
      return this.$t('tasks.detail.reopenRemaining', { hours: Math.floor(leftMinutes / 60), minutes: leftMinutes % 60 })
    },
  },
  created() {
    this.$store.dispatch('tasks/getDataItem', this.$route.params.id)
      .finally(() => { this.loaded = true })
  },
  methods: {
    confirmReopen() {
      if (!this.isReopenable) return
      this.$swal({
        title: this.$t('tasks.detail.reopenConfirmTitle'),
        text: this.$t('tasks.detail.reopenConfirmText'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('tasks.detail.reopenConfirmButton'),
        cancelButtonText: this.$t('tasks.detail.reopenConfirmCancel'),
        customClass: { confirmButton: 'btn btn-warning', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      }).then(async result => {
        if (!result.value) return
        this.saving = true
        try {
          const res = await this.$store.dispatch('tasks/reopenTask', this.taskItem.id)
          if (res?.status === false) throw new Error(res?.message || this.$t('tasks.detail.failed'))
          this.$toast({
            component: ToastificationContent,
            props: {
              variant: 'success', title: this.$t('common.toast.successShort'), icon: 'CheckIcon', text: this.$t('tasks.detail.reopened'),
            },
          })
          await this.$store.dispatch('tasks/getDataItem', this.taskItem.id)
        } catch (e) {
          this.$toast({
            component: ToastificationContent,
            props: {
              variant: 'danger', title: this.$t('common.toast.errorShort'), icon: 'XIcon', text: e?.message || this.$t('tasks.detail.failed'),
            },
          })
        } finally {
          this.saving = false
        }
      })
    },
    openCompleteModal() {
      if (!this.canManage || !this.canTransitionTo('completed')) return
      this.completeModal = true
    },
    onCompleteModalHidden() {
      this.closingNote = ''
      this.closingNoteError = null
      this.completeSaving = false
    },
    async submitComplete() {
      this.closingNoteError = null
      if (!this.closingNote.trim()) {
        this.closingNoteError = this.$t('tasks.detail.closingNoteRequired')
        return
      }
      this.completeSaving = true
      try {
        const res = await this.$store.dispatch('tasks/completeTask', {
          id: this.taskItem.id,
          closing_note: this.closingNote.trim(),
        })
        if (res?.status === false) {
          this.closingNoteError = res?.errors?.closing_note || res?.message || this.$t('tasks.detail.failed')
          return
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            variant: 'success', title: this.$t('common.toast.successShort'), icon: 'CheckIcon', text: this.$t('tasks.detail.completed'),
          },
        })
        this.completeModal = false
        await this.$store.dispatch('tasks/getDataItem', this.taskItem.id)
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            variant: 'danger', title: this.$t('common.toast.errorShort'), icon: 'XIcon', text: e?.message || this.$t('tasks.detail.failed'),
          },
        })
      } finally {
        this.completeSaving = false
      }
    },
    canTransitionTo(target) {
      const cur = this.currentStatus
      const active = cur === 'open' || cur === 'in_progress'
      if (target === 'in_progress') return cur === 'open'
      if (target === 'completed') return !this.hasOpenSubtasks && active
      if (target === 'cancelled') return active
      return false
    },
    async setStatus(status) {
      if (!this.canManage || !this.canTransitionTo(status)) return
      this.saving = true
      try {
        const res = await this.$store.dispatch('tasks/saveData', {
          id: this.taskItem.id,
          id_task_statuses: definitionId('task', status),
          completed_date: status === 'completed' ? this.moment().format('YYYY-MM-DD HH:mm:ss') : null,
          cancelled_date: status === 'cancelled' ? this.moment().format('YYYY-MM-DD HH:mm:ss') : null,
        })
        if (res?.status === false) {
          throw new Error(res?.message || this.$t('tasks.detail.statusUpdateFailed'))
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            variant: 'success', title: this.$t('common.toast.successShort'), icon: 'CheckIcon', text: this.$t('tasks.detail.statusChanged', { status: this.$t(`statuses.task.${status}`) }),
          },
        })
        await this.$store.dispatch('tasks/getDataItem', this.taskItem.id)
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            variant: 'danger', title: this.$t('common.toast.errorShort'), icon: 'XIcon', text: e?.message || this.$t('tasks.detail.statusUpdateFailed'),
          },
        })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
